import {
  MdMemory,
  MdStorage,
  MdComputer,
  MdNetworkWifi,
  MdInfo,
  MdLightbulb,
  MdDashboard,
} from "react-icons/md";

const SystemInfo = ({ systemData }) => {
  if (!systemData) {
    return (
      <div className="bg-primary-dark rounded-xl border border-gray-700 p-8 text-center">
        <p className="text-gray-400">No system information available</p>
      </div>
    );
  }

  const parseDiskValue = (diskStr) => {
    if (!diskStr) return { used: "N/A", percent: "0" };
    
    let percent = "0";
    let used = "N/A";
    
    const patterns = [
      /\((\d+)%\)/,
      /used \((\d+)%\)/,
      /(\d+)% used/,
      /(\d+)%\s+used/,
      /used\s+(\d+)%/,
      /(\d+)%\s+of/
    ];
    
    for (const pattern of patterns) {
      const match = diskStr.match(pattern);
      if (match) {
        percent = match[1];
        break;
      }
    }
    
    const usedPatterns = [
      /([\d.]+)G\s+used/,
      /used\s+([\d.]+)G/,
      /([\d.]+)G used/,
      /used\s+([\d.]+)Gi/
    ];
    
    for (const pattern of usedPatterns) {
      const match = diskStr.match(pattern);
      if (match) {
        used = match[1];
        break;
      }
    }
    
    return { used, percent };
  };

  return (
      <div className="p-4 sm:p-6 md:p-8">
        <div className="bg-gray-800/30 rounded-xl border border-gray-700 p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <MdDashboard className="text-primary-cyan text-xl sm:text-2xl" />
            <h4 className="text-white font-semibold text-base sm:text-lg">
              System Overview
            </h4>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-gray-800/50 rounded-xl p-4 sm:p-5 border border-gray-700 hover:border-primary-cyan/30 transition">
              <div className="flex items-center gap-2 mb-3">
                <MdMemory className="text-primary-cyan text-xl sm:text-2xl" />
                <h4 className="text-gray-300 font-semibold text-sm sm:text-base">Memory</h4>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm break-words">
                {systemData.memory || "N/A"}
              </p>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-4 sm:p-5 border border-gray-700 hover:border-primary-cyan/30 transition">
              <div className="flex items-center gap-2 mb-3">
                <MdStorage className="text-primary-cyan text-xl sm:text-2xl" />
                <h4 className="text-gray-300 font-semibold text-sm sm:text-base">Disk</h4>
              </div>
              <div className="space-y-2">
                <p className="text-gray-400 text-xs sm:text-sm break-words">
                  {systemData.disk || "N/A"}
                </p>
                {(() => {
                  const diskInfo = parseDiskValue(systemData.disk || "");
                  const percentNum = parseInt(diskInfo.percent);
                  if (percentNum > 0 && percentNum <= 100) {
                    return (
                      <div className="mt-2 pt-2 border-t border-gray-700">
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-gray-500">Used</span>
                          <span className="text-primary-cyan font-medium">
                            {diskInfo.percent}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-1.5">
                          <div
                            className="bg-primary-cyan h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${percentNum}%` }}
                          />
                        </div>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-4 sm:p-5 border border-gray-700 hover:border-primary-cyan/30 transition">
              <div className="flex items-center gap-2 mb-3">
                <MdComputer className="text-primary-cyan text-xl sm:text-2xl" />
                <h4 className="text-gray-300 font-semibold text-sm sm:text-base">CPU</h4>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm break-words">
                {systemData.cpu || "N/A"}
              </p>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-4 sm:p-5 border border-gray-700 hover:border-primary-cyan/30 transition">
              <div className="flex items-center gap-2 mb-3">
                <MdNetworkWifi className="text-primary-cyan text-xl sm:text-2xl" />
                <h4 className="text-gray-300 font-semibold text-sm sm:text-base">Network</h4>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm break-words">
                {systemData.network || "N/A"}
              </p>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-4 sm:p-5 border border-gray-700 hover:border-primary-cyan/30 transition">
              <div className="flex items-center gap-2 mb-3">
                <MdInfo className="text-primary-cyan text-xl sm:text-2xl" />
                <h4 className="text-gray-300 font-semibold text-sm sm:text-base">Details</h4>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm break-words leading-relaxed">
                {systemData.details || "N/A"}
              </p>
            </div>

            <div className="bg-gray-800/50 rounded-xl p-4 sm:p-5 border border-gray-700 hover:border-primary-cyan/30 transition">
              <div className="flex items-center gap-2 mb-3">
                <MdLightbulb className="text-primary-cyan text-xl sm:text-2xl" />
                <h4 className="text-gray-300 font-semibold text-sm sm:text-base">Suggestion</h4>
              </div>
              <p className="text-primary-cyan font-medium text-xs sm:text-sm break-words leading-relaxed">
                {systemData.suggestion || "N/A"}
              </p>
            </div>
          </div>
        </div>
      </div>
  );
};

export default SystemInfo;